/**
 * src/hooks/useCustomerRole.ts
 *
 * Thin hook that exposes CustomerRole from the authenticated Customer record.
 * Wraps useCustomerProfile() — no separate profile fetch, HarperDB-backed.
 *
 * WP-ATTICDEV-031-ROLE (T2 — Story 2):
 *   AC-005: HarperDB is the sole source of truth for role; refetch() after
 *           a successful setRole() keeps this hook in sync with the DB.
 *   AC-006: Returns { role: CustomerRole | null, setRole, loading } exactly.
 *   AC-007: Returns null for null/undefined/unknown role values.
 *           Does NOT coerce null → 'customer'; callers decide null semantics.
 *   AC-008: customer_id sourced exclusively from useCustomerProfile → useAuth;
 *           never from a prop, URL param, or external input (RBAC safety).
 */

import { useCallback } from 'react';
import { useCustomerProfile } from './useCustomerProfile';
import { updateCustomerRole } from '../api/customerApi';
import { narrowToCustomerRole } from '../types';
import type { CustomerRole } from '../types';

export interface UseCustomerRoleResult {
  /**
   * The authenticated user's role from the HarperDB Customer record, or null when:
   * - the Customer record has no role field (legacy record pre-WP-031), or
   * - the raw role value is not a known CustomerRole (e.g. legacy 'admin').
   *
   * Callers that want to treat null as 'customer' must do so at the call site.
   * This hook never silently coerces null → 'customer' (AC-007).
   */
  role: CustomerRole | null;

  /**
   * Persists a new role to the Customer record in HarperDB.
   * Resolves to true on success, false on failure.
   *
   * AC-008: customer_id is sourced from the authenticated session only
   * (useCustomerProfile → useAuth().customerEmail → Customer.id).
   * It is never sourced from a prop, URL parameter, or external input.
   */
  setRole: (r: CustomerRole) => Promise<boolean>;

  /**
   * True while the Customer profile is being fetched from HarperDB.
   * Mirrors useCustomerProfile().loading — no separate network call is made.
   */
  loading: boolean;
}

/**
 * useCustomerRole — role slice of the authenticated Customer record.
 *
 * Delegates all profile fetching to useCustomerProfile so there are no
 * duplicate API calls. The hook computes role by narrowing the raw string
 * value from HarperDB through narrowToCustomerRole().
 */
export function useCustomerRole(): UseCustomerRoleResult {
  const { profile, loading, refetch } = useCustomerProfile();

  const setRole = useCallback(
    async (r: CustomerRole): Promise<boolean> => {
      // AC-008: profile.id traces through useCustomerProfile -> Customer record lookup
      // { email: customerEmail }) → useAuth().customerEmail. It is session-owned,
      // never prop-passed or URL-sourced.
      if (!profile?.id) return false;

      const result = await updateCustomerRole(profile.id, r);

      if (result.success) {
        // Refetch the Customer record so this hook's `role` value reflects the
        // persisted HarperDB state (AC-005: source of truth is HarperDB, not
        // any local variable or localStorage entry).
        refetch();
      }

      return result.success;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile?.id, refetch],
  );

  return {
    // AC-007: narrowToCustomerRole returns null for null / undefined / unknown values.
    // Example: a legacy record with role === 'admin' → null, not 'customer'.
    role: narrowToCustomerRole(profile?.role),
    setRole,
    loading,
  };
}
